'use client';

import { memo, RefCallbackWithCleanup, useCallback, type FC } from 'react';

export interface IInternalApplePayButtonProps
  extends Omit<
    HTMLApplePayButtonElementAttributes,
    'class' | 'for' | 'buttonstyle'
  > {
  /**
   * Callback to be executed when the Apple Pay button is clicked.
   * @param event - The mouse event that triggered the click.
   */
  onClick?: (event: MouseEvent) => void;

  /**
   * Additional classes to apply to the Apple Pay button element.
   */
  className?: string;

  /**
   * The theme of the Apple Pay button.
   * @default 'black'
   * @see {@link https://developer.apple.com/documentation/apple_pay_on_the_web/applepaybutton/3820127-buttonstyle buttonstyle}
   */
  buttonStyle?: HTMLApplePayButtonElementAttributes['buttonstyle'];
}

/**
 * A React wrapper component for the Apple Pay button web component provided
 * by the Apple Pay JS SDK. It provides a simple API for handling click events
 * and customizing the button's appearance.
 *
 * It does **not** automatically load the Apple Pay JS SDK or integrate with
 * the `ApplePaySession` API out of the box.
 * @see {@link ../ApplePayButton.tsx}
 */
export const InternalApplePayButton: FC<IInternalApplePayButtonProps> = memo(
  function ApplePayButton({ onClick, className, buttonStyle, ...attrs }) {
    const onClickRef = useCallback<
      RefCallbackWithCleanup<HTMLApplePayButtonElement>
    >(
      (btn) => {
        if (!onClick) return () => {};

        btn.addEventListener('click', onClick);
        return () => {
          btn.removeEventListener('click', onClick);
        };
      },
      [onClick]
    );

    return (
      <apple-pay-button
        ref={onClickRef}
        class={className}
        buttonstyle={buttonStyle}
        {...attrs}
      />
    );
  }
);
